import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import api, { formatINR, API_BASE } from "@/lib/api";
import { SkeletonRow } from "@/components/Loaders";
import { EmptyState } from "@/components/EmptyState";
import { ClipboardText, DownloadSimple, WhatsappLogo, LinkSimple } from "@phosphor-icons/react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MagnifyingGlassPlus } from "@phosphor-icons/react";
import { BOOKING_STATUS_STYLE as STATUS_STYLE, fmtStatus } from "@/constants/status";

const STATUSES = ["pending", "awaiting_payment", "confirmed", "rejected", "cancelled", "completed", "payment_failed", "payment_expired", "refund_pending", "refunded"];

export default function AdminBookings() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [markTarget, setMarkTarget] = useState(null);

  const confirmMarkPaid = async () => {
    if (!markTarget) return;
    try {
      await api.post(`/bookings/${markTarget.b.id}/mark-paid-link`, { type: markTarget.type });
      toast.success(markTarget.type === "advance" ? "Advance marked paid — booking confirmed" : "Balance marked paid");
      setMarkTarget(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const load = () => { setItems(null); api.get(`/admin/bookings${filter ? `?status=${filter}` : ""}`).then((r) => setItems(r.data)); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const openDetail = (id) => api.get(`/admin/bookings/${id}/finance`).then((r) => setDetail(r.data)).catch(() => toast.error("Couldn't load finance breakdown"));

  const change = async (id, status) => {
    await api.patch(`/admin/bookings/${id}/status`, { status });    toast.success("Status updated");
    load();
  };

  return (
    <DashboardLayout nav={adminNav} title="Admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">All Bookings</h1>
        <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="ab-filter" className="w-44 rounded-full"><SelectValue placeholder="Filter" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{fmtStatus(s)}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-stone-200 bg-white">
        {items === null ? (
          <div className="space-y-2 p-4">{[1, 2, 3].map((i) => <SkeletonRow key={i} />)}</div>
        ) : items.length === 0 ? (
          <EmptyState icon={ClipboardText} title="No bookings" message="No bookings match this filter yet." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Booking ID</TableHead><TableHead>Property</TableHead><TableHead>Customer</TableHead>
                <TableHead>Date</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Commission</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Receipt</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((b) => (
                <TableRow key={b.id} data-testid={`admin-booking-${b.id}`}>
                  <TableCell className="font-mono text-xs">{b.booking_ref}</TableCell>
                  <TableCell>{b.property_name}</TableCell>
                  <TableCell>{b.customer_name}<br /><span className="text-xs text-stone-400">{b.customer_phone}</span></TableCell>
                  <TableCell>{b.date}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatINR(b.total_amount)}</TableCell>
                  <TableCell className="text-right tabular-nums text-primary">{formatINR(b.commission_amount)}</TableCell>
                  <TableCell>
                    <Select value={b.status} onValueChange={(v) => change(b.id, v)}>
                      <SelectTrigger data-testid={`ab-status-${b.id}`} className={`h-8 w-40 rounded-full border-0 text-xs font-semibold ${STATUS_STYLE[b.status]}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{fmtStatus(s)}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {(((b.payment_status !== "paid") && ["awaiting_payment", "payment_failed"].includes(b.status)) || (b.status === "confirmed" && b.payment_status === "paid" && !b.balance_paid && Number(b.balance_amount) > 0)) && (
                        <button data-testid={`ab-markpaid-${b.id}`} title="Mark paid via Razorpay link"
                          onClick={() => setMarkTarget({ b, type: b.payment_status !== "paid" ? "advance" : "balance" })}
                          className="rounded-lg border border-primary/40 p-1.5 text-primary hover:bg-accent"><LinkSimple size={16} /></button>
                      )}
                      <button data-testid={`ab-finance-${b.id}`} title="Finance breakdown" onClick={() => openDetail(b.id)}
                        className="rounded-lg border border-stone-200 p-1.5 text-primary hover:bg-accent"><MagnifyingGlassPlus size={16} /></button>
                      <button data-testid={`ab-pdf-${b.id}`} title="Download receipt PDF"
                        onClick={() => {
                          const token = localStorage.getItem("avb_token");
                          fetch(`${API_BASE}/admin/bookings/${b.id}/invoice/pdf`, { headers: { Authorization: `Bearer ${token}` } })
                            .then((r) => r.blob()).then((bl) => {
                              const url = URL.createObjectURL(bl);
                              const a = document.createElement("a"); a.href = url; a.download = `receipt_${b.booking_ref}.pdf`; a.click();
                              URL.revokeObjectURL(url);
                            }).catch(() => toast.error("Couldn't generate PDF"));
                        }}
                        className="rounded-lg border border-stone-200 p-1.5 text-stone-600 hover:bg-accent"><DownloadSimple size={16} /></button>
                      <button data-testid={`ab-whatsapp-${b.id}`} title="Send receipt on WhatsApp"
                        onClick={() => api.post(`/admin/bookings/${b.id}/send-receipt-whatsapp`, {})
                          .then((r) => toast.success(r.data.whatsapp?.simulated ? "WhatsApp simulated (add Twilio keys to send)" : "Receipt sent on WhatsApp"))
                          .catch((e) => toast.error(e.response?.data?.detail || "Failed"))}
                        className="rounded-lg border border-stone-200 p-1.5 text-green-600 hover:bg-accent"><WhatsappLogo size={16} /></button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="booking-finance-dialog" className="max-w-md">
          <DialogHeader><DialogTitle>Finance breakdown {detail?.breakdown?.booking_ref ? `— ${detail.breakdown.booking_ref}` : ""}</DialogTitle></DialogHeader>
          {detail && (() => {
            const bd = detail.breakdown;
            const Line = ({ label, value, color, strong }) => (
              <div className={`flex justify-between py-1.5 text-sm ${strong ? "font-semibold" : ""}`}>
                <span className="text-stone-500">{label}</span>
                <span className={`tabular-nums ${color || "text-stone-800"}`}>{formatINR(value)}</span>
              </div>
            );
            return (
              <div className="divide-y divide-stone-100" data-testid="booking-finance-body">
                <div className="pb-1">
                  <Line label="Base amount" value={bd.base_amount} />
                  {bd.service_fee > 0 && <Line label="Service fee" value={bd.service_fee} />}
                  {bd.discount_amount > 0 && <Line label={`Discount${bd.coupon_code ? ` (${bd.coupon_code})` : ""}`} value={-bd.discount_amount} color="text-red-600" />}
                  {bd.tax_amount > 0 && <Line label="Tax" value={bd.tax_amount} />}
                  <Line label="Customer total" value={bd.customer_total} strong />
                </div>
                <div className="py-1">
                  <Line label="Advance paid" value={bd.advance_amount} />
                  <Line label={`Balance ${bd.balance_paid ? "(paid)" : "(at venue)"}`} value={bd.balance_amount} />
                  <Line label="Collected online" value={bd.collected_online} />
                </div>
                <div className="py-1">
                  <Line label={`Avinya commission (${bd.commission_percentage}% · ${bd.commission_source || "default"})`} value={bd.avinya_revenue} color="text-primary" />
                  <Line label="Owner earnings" value={bd.owner_earnings} />
                  {bd.refund_amount > 0 && <Line label="Refund" value={bd.refund_amount} color="text-red-600" />}
                  <Line label="Owner net payout" value={bd.payout_net} strong color="text-green-700" />
                </div>
                <div className="flex flex-wrap gap-2 pt-3 text-xs">
                  <span className="rounded-full bg-stone-100 px-2 py-0.5 uppercase">Payment: {bd.payment_status}</span>
                  <span className="rounded-full bg-stone-100 px-2 py-0.5 uppercase">Booking: {bd.booking_status}</span>
                  {detail.payout && <span className="rounded-full bg-blue-100 px-2 py-0.5 text-blue-700">Payout: {detail.payout.status}</span>}
                  {bd.commission_locked && <span className="rounded-full bg-green-100 px-2 py-0.5 text-green-700">Commission locked</span>}
                </div>
                {bd.payment_reference && <p className="pt-2 text-xs text-stone-400">Payment ref: {bd.payment_reference}</p>}
                {detail.booking?.receipt_sent_at && <p className="pt-1 text-xs text-stone-400" data-testid="receipt-sent-log">Receipt last sent: {new Date(detail.booking.receipt_sent_at).toLocaleString()}{detail.booking.receipt_sends?.length ? ` · ${detail.booking.receipt_sends.length}×` : ""}</p>}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={!!markTarget} onOpenChange={(v) => !v && setMarkTarget(null)}>
        <DialogContent data-testid="ab-mark-paid-dialog">
          <DialogHeader><DialogTitle>Mark {markTarget?.type} paid via Razorpay link?</DialogTitle></DialogHeader>
          {markTarget && (
            <p className="text-sm text-stone-600">Confirm <span className="font-heading font-bold text-primary">{formatINR(markTarget.type === "advance" ? markTarget.b.advance_amount : markTarget.b.balance_amount)}</span> was received for <span className="font-mono">{markTarget.b.booking_ref}</span> via the Razorpay payment link. {markTarget.type === "advance" ? "This will CONFIRM the booking." : "This clears the balance."} Only do this after the money is actually in the Razorpay account.</p>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setMarkTarget(null)}>Cancel</Button>
            <Button data-testid="ab-confirm-mark-paid" className="rounded-xl bg-primary hover:bg-green-900" onClick={confirmMarkPaid}>Yes, mark paid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
